export type VoiceEngineId =
  | "economy"
  | "standard"
  | "basic"
  | "better"
  | "complex"
  | "max";

export interface VoiceEngineOption {
  id: VoiceEngineId;
  tier: string;
  reasoning: string;
  description: string;
  internalModel: string;
  reasoningEffort?: "minimal" | "low" | "medium" | "high" | "xhigh";
  estimatedAiCostPerMinute: number;
  inboundCost: number;
  suggestedInboundPrice: number;
  auLocalOutboundCost: number;
  suggestedLocalOutboundPrice: number;
  auMobileOutboundCost: number;
  suggestedMobileOutboundPrice: number;
}

export const DEFAULT_VOICE_ENGINE_ID: VoiceEngineId = "standard";

export const VOICE_ENGINE_OPTIONS: VoiceEngineOption[] = [
  {
    id: "economy",
    tier: "Economy",
    reasoning: "realtime-mini",
    description: "Lower-cost calls where speed and simple routing matter most.",
    internalModel: "realtime-mini",
    estimatedAiCostPerMinute: 0.025,
    inboundCost: 0.04,
    suggestedInboundPrice: 0.09,
    auLocalOutboundCost: 0.055,
    suggestedLocalOutboundPrice: 0.11,
    auMobileOutboundCost: 0.105,
    suggestedMobileOutboundPrice: 0.16
  },
  {
    id: "standard",
    tier: "Standard",
    reasoning: "Realtime-2 low",
    description: "Recommended default for normal production calls and tool use.",
    internalModel: "gpt-realtime-2",
    reasoningEffort: "low",
    estimatedAiCostPerMinute: 0.075,
    inboundCost: 0.09,
    suggestedInboundPrice: 0.15,
    auLocalOutboundCost: 0.105,
    suggestedLocalOutboundPrice: 0.16,
    auMobileOutboundCost: 0.155,
    suggestedMobileOutboundPrice: 0.22
  },
  {
    id: "basic",
    tier: "Basic reasoning",
    reasoning: "Realtime-2 minimal",
    description: "Fastest Realtime-2 mode for FAQs, booking capture, and routing.",
    internalModel: "gpt-realtime-2",
    reasoningEffort: "minimal",
    estimatedAiCostPerMinute: 0.06,
    inboundCost: 0.075,
    suggestedInboundPrice: 0.13,
    auLocalOutboundCost: 0.09,
    suggestedLocalOutboundPrice: 0.15,
    auMobileOutboundCost: 0.14,
    suggestedMobileOutboundPrice: 0.2
  },
  {
    id: "better",
    tier: "Better reasoning",
    reasoning: "Realtime-2 medium",
    description: "Stronger context handling for multi-step calls.",
    internalModel: "gpt-realtime-2",
    reasoningEffort: "medium",
    estimatedAiCostPerMinute: 0.095,
    inboundCost: 0.11,
    suggestedInboundPrice: 0.18,
    auLocalOutboundCost: 0.125,
    suggestedLocalOutboundPrice: 0.2,
    auMobileOutboundCost: 0.175,
    suggestedMobileOutboundPrice: 0.27
  },
  {
    id: "complex",
    tier: "Complex reasoning",
    reasoning: "Realtime-2 high",
    description: "More deliberate handling for complex workflows and recovery.",
    internalModel: "gpt-realtime-2",
    reasoningEffort: "high",
    estimatedAiCostPerMinute: 0.125,
    inboundCost: 0.14,
    suggestedInboundPrice: 0.23,
    auLocalOutboundCost: 0.155,
    suggestedLocalOutboundPrice: 0.25,
    auMobileOutboundCost: 0.205,
    suggestedMobileOutboundPrice: 0.32
  },
  {
    id: "max",
    tier: "Max reasoning",
    reasoning: "Realtime-2 xhigh",
    description: "Highest reasoning setting for the hardest call flows.",
    internalModel: "gpt-realtime-2",
    reasoningEffort: "xhigh",
    estimatedAiCostPerMinute: 0.17,
    inboundCost: 0.185,
    suggestedInboundPrice: 0.3,
    auLocalOutboundCost: 0.2,
    suggestedLocalOutboundPrice: 0.33,
    auMobileOutboundCost: 0.25,
    suggestedMobileOutboundPrice: 0.4
  }
];

export const OUTBOUND_CAMPAIGN_PRICING = {
  reservedConcurrencyRate: 0.005,
  minimumReservationMinutes: 30,
  transferNote: "Transfers to PSTN add the second outbound leg for the transfer duration."
};

export function findVoiceEngineOption(id: unknown): VoiceEngineOption {
  const normalized = typeof id === "string" ? id : "";
  return VOICE_ENGINE_OPTIONS.find((option) => option.id === normalized) || VOICE_ENGINE_OPTIONS[1];
}

export function formatMinuteRate(value: number, fractionDigits = 3): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  }).format(value);
}

export function formatCostRate(value: number): string {
  return formatMinuteRate(value, 3);
}

export function formatCustomerRate(value: number): string {
  return formatMinuteRate(value, 2);
}
